import { DropdownOption } from '../components/rounded-dropdown/models/dropdown.models';

export interface ToolsServicesOverviewResponse {
  tools: {
    messageSummary: SolutionMessageSummary;
    required: RequiredSolution[];
    existing: ExistingSolution[];
    tabBadgeCount: number;
  };
  services: {
    messageSummary: SolutionMessageSummary;
    required: RequiredSolution[];
    existing: ExistingSolution[];
    tabBadgeCount: number;
  };
  /** Whether the current user may add tools / services to the stack (Owner/Admin only). */
  canAddSolution: boolean;
}

export enum MessageSummaryMode {
  HIDDEN = 0,
  WARNING = 1,
  ERROR = 2,
}

export interface SolutionMessageSummary {
  mode: MessageSummaryMode;
  reasons: {
    reason: MessageReason;
    severity: MessageSeverity;
    count: number;
    messageId: string;
    messageType: 'SystemMessage' | string;
    messageContent: 'ExpiringLicenses';
    messageArgs: { [key: string]: string | number };
  }[];
  counts: {
    missing: {
      warning: number;
      error: number;
      total: number;
    };
    expiring: {
      warning: number;
      error: number;
      total: number;
    };
  };
}

export interface ExistingSolution {
  solutionId: string;
  vendor: string;
  productOrServiceName: string;
  licenseName: string;
  licenseStartDate: string;
  licenseEndDate: string;
  licenseCost: number;
  price: number;
  isInStack: boolean;
  serviceType?: string;
  hasIssue: boolean;
}

export interface RequiredSolution {
  solutionId: string;
  vendorId: string;
  recommendedLicenseTypeId?: string;
  vendor: string;
  productOrServiceName: string;
  licenseRequired: string;
  msrp: number;
  acquireBy: string;
  length: number;
  actionRequired: RequiredToolAndServiceAction;
  isInStack: boolean;
  serviceType?: string;
  integrationStatus?: IntegrationStatus;
}

export enum IntegrationStatus {
  NOT_INTEGRATED = 0,
  PENDING_INTEGRATION,
  INTEGRATED,
  WAITING_FOR_USER_CONSENT,
  PENDING_EVENT_APPROVAL,
  FAILED,
  NEEDS_USER_ACTION,
  DISCONNECTED,
  EXPIRED,
}

export interface AddRemediationSolutionDTO {
  type: RemediationSolutionType;
  mode: RemediationSolutionMode;
  solutionId?: string;
  licenseTypeId?: string;
  vendorId?: string;
  manualVendor?: string;
  manualSolution?: string;
  manualLicenseName?: string;
  startMonthUtc: string;
  endMonthUtc: string;
  cost: number;
  features: string[];
  requiredEntryId?: string;
}

export interface ViewRemediationSolutionDTO {
  solutionId: string;
  type: RemediationSolutionType;
  vendor: string;
  name: string;
  description: string;
  license: {
    licenseId: string;
    typeName: string;
    startUtc: string;
    endUtc: string;
    cost: number;
  };
  banner: {
    mode: MessageMode;
    reasons: {
      reason: MessageReason;
      severity: MessageSeverity;
      messageId: string;
      messageType: string;
      messageArgs: { daysLeft: number };
    }[];
  };
  integrationStatus?: IntegrationStatus;
  supportsIntegration?: boolean;
  /** Whether the current user may connect, configure or remove this solution's integration (Owner/Admin only). */
  canChangeIntegration: boolean;
}

export interface IntegrationDetails {
  connectSupported: boolean;
  integrationFields: IntegrationField[];
  unsupportedFlow?: boolean;
}

export type IntegrationSubSidebarMode = 'add' | 'view' | 'edit';
export enum IntegrationType {
  DisoUserConnect = 'DisoUserConnect',
  IntegrationManager = 'IntegrationManager',
}

export interface IntegrationSubmitPayload {
  solutionId: string;
  /** In edit mode the client may send only changed fields; add mode sends all. */
  values: Record<string, string | boolean>;
}

/** `POST .../integration/connect` 200 body. `status` is Pending while the connect test runs; Failed / Connected are already final. */
export interface IntegrationConnectResponse {
  runId?: string;
  status?: string;
  errorCode?: string | null;
  messageId?: string | null;
}

export interface IntegrationFieldDTO {
  key: string;
  label: string;
  type: string;
  required: boolean;
  secret: boolean;
  description: string;
  defaultValue: string;
}

export interface IntegrationField {
  fieldType: IntegrationFieldType;
  value: string | string[];
  label: string;
  disabled: boolean;
  name: string;
  isMandatory: boolean;
  options?: DropdownOption[];
  placeholder?: string;
  description?: string;
}

/** String-valued connect-flow/session status, as returned by the connect-metadata,
 * user-connect start/callback, and connection-status endpoints. Distinct from the
 * legacy numeric `IntegrationStatus` used by RequiredSolution/ViewRemediationSolutionDTO. */
export enum ToolConnectionStatus {
  NotConnected = 'NotConnected',
  PendingIMValidation = 'PendingIMValidation',
  Connected = 'Connected',
  WaitingForUserConsent = 'WaitingForUserConsent',
  PendingEventApproval = 'PendingEventApproval',
  Failed = 'Failed',
  NeedsUserAction = 'NeedsUserAction',
  Disconnected = 'Disconnected',
  Expired = 'Expired',
}

export const TERMINAL_TOOL_CONNECTION_STATUSES: ReadonlySet<ToolConnectionStatus> = new Set([
  ToolConnectionStatus.Connected,
  ToolConnectionStatus.Failed,
  ToolConnectionStatus.NeedsUserAction,
  ToolConnectionStatus.Disconnected,
  ToolConnectionStatus.Expired,
]);

export interface ConnectMetadata {
  integrationServiceId: string;
  toolId: string;
  connectSupported: boolean;
  connectFlow: IntegrationType;
  imConnectRequired: boolean;
  pluginId?: string;
  actionId?: string;
  actionKey?: string;
  actionModelType?: string;
  fields: IntegrationFieldDTO[];
  requiredUserParameters: RequiredUserParametersForConnect[];
  actions: ActionForConnect[];
  userMessages: UserMessageForConnect[];
  connectionStatus?: ToolConnectionStatus;
  sourceContainerMasterId?: string;
  sourceContainerName?: string;
  sessionTtlSeconds?: number;
}

/** Promotes several fields (ConnectFlow, SourceContainerMasterId/Name, ImConnectRequired,
 * SessionTtlSeconds) to the top level alongside the nested `metadata`, so FE doesn't need to
 * reach into `metadata` for them — confirmed against backend `ConnectMetadataResponse` (Gap 7). */
export interface ConnectMetadataResponse {
  connectFlow: IntegrationType;
  toolId: string;
  integrationServiceId: string;
  connectionStatus: ToolConnectionStatus;
  sourceContainerMasterId?: string;
  sourceContainerName?: string;
  imConnectRequired: boolean;
  sessionTtlSeconds?: number;
  metadata: ConnectMetadata;
}

export interface UserConnectStartResponse {
  sessionId: string;
  state: string;
  redirectUrl: string;
  expiresAtUtc: string;
}

export interface UserConnectCallbackRequest {
  state: string;
  providerCallback: Record<string, string>;
}

export interface UserConnectCallbackResponse {
  connectionStatus: ToolConnectionStatus;
  integrationServiceId: string;
  toolId: string;
  eventMSConnectRunId?: string;
  safeErrorMessage?: string;
}

export interface ConnectionStatusResponse {
  toolId: string;
  status: ToolConnectionStatus;
  eventMSConnectRunId?: string;
  safeErrorMessage?: string;
  connectedAtUtc?: string;
}

export interface UserMessageForConnect {
  language: LanguageForConnect;
  message: string;
}

export interface ActionForConnect {
  key: string;
  label: string;
  type: string;
  /** Present on the wire for future action types; FE must never build/decode a URL from this
   * itself (security requirement) — always use the backend-generated redirectUrl from the
   * user-connect/start response instead. */
  urlTemplate?: string;
}

export interface RequiredUserParametersForConnect {
  key: string;
  label: string;
  type: RequiredUserParametersForConnectType;
  required: boolean;
  description?: string;
}

export enum RequiredUserParametersForConnectType {
  STRING = 'string',
  STRING_ARRAY = 'string-array',
  BOOLEAN = 'boolean',
  NUMBER = 'number',
  URL = 'url',
  EMAIL = 'email',
  PHONE = 'phone',
  DATE = 'date',
}

export enum LanguageForConnect {
  EN = 'en',
  HEB = 'he',
  AR = 'ar',
}

export enum IntegrationFieldType {
  TEXT = 'Text',
  PASSWORD = 'Password',
  DROPDOWN = 'Dropdown',
  CHECKBOX = 'Checkbox',
  NUMBER = 'Number',
  URL = 'Url',
  STRING_ARRAY = 'StringArray',
}

export enum RemediationSolutionType {
  TOOL = 'Tool',
  SERVICE = 'Service',
}

export enum RemediationSolutionMode {
  PRESET_DETERMINED = 'PreSetDetermined',
  MANUAL_SOLUTION = 'ManualSolution',
  MANUAL_LICENSE = 'ManualLicense',
}

export enum MessageMode {
  HIDDEN = 'Hidden',
  WARNING = 'Warning',
  ERROR = 'Error',
}

export enum MessageReason {
  MISSING_SOLUTIONS = 'MissingSolutions',
  EXPIRING_LICENSES = 'ExpiringSoon',
  LICENSE_STARTS_LATE = 'LicenseStartsLate',
}

export enum MessageSeverity {
  WARNING = 'Warning',
  ERROR = 'Error',
}

export enum RequiredToolAndServiceAction {
  ADD_TO_STACK = 0,
  UPDATE_LICENSE = 1,
}
